import {computed, inject, Injectable, isDevMode} from '@angular/core';
import { CheckStatus, ReworkStatus } from '../../../core/models/checks-model';
import { CheckStepperService } from './check-stepper';
import { RoleType } from '../../../core/models/role-type';
import { CurrentUserService } from '../../../services/current-user';
import { PendingCheckContextStore } from '../pending-checks/store/pending-check-context.store';
import { PendingCheckCommentStore } from '../pending-checks/store/pending-check-comment.store';

/**
 * PendingCheckPermissionService
 *
 * Kapselt alle Berechtigungs- und RBAC-Computeds für die PendingChecks-Ansicht.
 * Abhängig vom PendingCheckContextStore, PendingCheckCommentStore und CheckStepperService
 * (Signal-Store-Architektur – konsistent mit PendingCheckFacadeStore).
 *
 * Wird via providedIn: 'root' bereitgestellt.
 */
@Injectable({ providedIn: 'root' })
export class PendingCheckPermissionService {
  private readonly contextStore = inject(PendingCheckContextStore);
  private readonly commentStore = inject(PendingCheckCommentStore);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly checkStepperService = inject(CheckStepperService);

  // --- RBAC: Wer ist je nach Phase der aktive Prüfer? ---
  readonly activeUserForPhase = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm) return null;
    if (vm.status === CheckStatus['Kontrolle 1-1']) return vm.checker11;
    if (vm.status === CheckStatus['Kontrolle 1-2']) return vm.checker12;
    if (vm.status === CheckStatus['Kontrolle 2']) return vm.checker2;
    return null;
  });

  readonly isCurrentUserActiveChecker = computed(() => {
    // DEV-MODE: Buttons immer sichtbar, damit der Workflow ohne echten User testbar ist.
    // ToDo: Bessere alternative für isDevMode finden
    // if (isDevMode()) return true;
    const active = this.activeUserForPhase();
    return active != null && this.currentUserService.currentUser()?.oid === active.oid;
  });

  readonly isCurrentUserCreator = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    return vm != null && this.currentUserService.currentUser()?.oid === vm.creator?.oid;
  });

  readonly isCurrentUserChecker11 = computed(() => {
    // DEV-MODE: Rückläufer-Button immer sichtbar.
    // if (isDevMode()) return true;
    const vm = this.contextStore.checkInfoViewModel();
    return vm != null && this.currentUserService.currentUser()?.oid === vm.checker11?.oid;
  });

  readonly isCurrentUserProjectManagerOrProjectDeputyManager = computed(() => {
    return this.currentUserService.currentUser()?.roles.some(
      r => r === RoleType.PROJECT_MANAGER || r === RoleType.PROJECT_DEPUTY_MANAGER
    ) ?? false;
  });

  readonly isInRework = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm) return false;
    return vm.reworkStatus !== ReworkStatus.None;
  });

  /**
   * Bestimmt ob ein Kommentar in der aktuellen Phase sichtbar sein soll.
   */
  private isCommentVisibleForPhase(
    commentCheckStatus: CheckStatus,
    currentCheckStatus: CheckStatus,
    reworkStatus: ReworkStatus
  ): boolean {
    if (currentCheckStatus <= CheckStatus['Kontrolle 1-2']) {
      return commentCheckStatus <= CheckStatus['Kontrolle 1-2'];
    }
    if (currentCheckStatus === CheckStatus['Kontrolle 2'] && reworkStatus !== ReworkStatus.None) {
      return commentCheckStatus <= CheckStatus['Kontrolle 2'];
    }
    return commentCheckStatus === currentCheckStatus;
  }

  // --- Kommentar-Status ---
  readonly commentsResolved = computed(() => {
    const currentCheck = this.contextStore.currentCheck();
    if (!currentCheck) return false;
    // Kommentare sind bereits check-spezifisch geladen (GET /check/{id}/comment)
    const checkComments = this.commentStore.visibleComments().filter(c =>
      this.isCommentVisibleForPhase(c.checkStatus, currentCheck.status, currentCheck.reworkStatus)
    );
    if (checkComments.length === 0) return true;
    return checkComments.every(c => c.status > 0);
  });


  readonly hasOnlyOwnUnresolvedComments = computed(() => {
    const currentCheck = this.contextStore.currentCheck();
    if (!currentCheck) return false;
    const unresolvedComments = this.commentStore.visibleComments().filter(c =>
      this.isCommentVisibleForPhase(c.checkStatus, currentCheck.status, currentCheck.reworkStatus) &&
      c.status === 0
    );
    if (unresolvedComments.length === 0) return false;
    return unresolvedComments.every(c => c.author?.oid === this.currentUserService.currentUserOid());
  });

  readonly canResolveRework = computed(() => {
    if (!this.isInRework()) return false;
    if (!this.isCurrentUserChecker11()) return false;
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm) return false;

    if (vm.reworkStatus === ReworkStatus.PruefungNichtMoeglich) {
      // Der Stage-Status (CHECK_IMPOSSIBLE) ist die Quelle der Wahrheit – nicht ob ein
      // CRITICAL-Kommentar existiert. CRITICAL-Kommentare müssen erledigt sein, falls vorhanden.
      // isCommentVisibleForPhase statt exaktem checkStatus-Vergleich: wenn das Backend kein
      // stage-Objekt im Comment-Response einbettet, defaultet checkStatus auf 0 (K1-1).
      const visibleImpossibleComments = this.commentStore.visibleComments().filter(c =>
        c.isCheckImpossible &&
        this.isCommentVisibleForPhase(c.checkStatus, vm.status, vm.reworkStatus)
      );
      const impossibleResolved = visibleImpossibleComments.every(c => c.status > 0);
      return impossibleResolved && this.commentsResolved();
    }
    return this.commentsResolved();
  });

  readonly isReadOnly = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm) return true;
    // DEV-MODE: Nie read-only, damit der komplette Workflow testbar ist.
    // if (isDevMode()) return false;
    if (vm.status === CheckStatus.Abgeschlossen) return true;
    // Während Rework darf NUR checker11 bearbeiten – Prüfer und Creator sind gesperrt
    if (this.isInRework()) return !this.isCurrentUserChecker11();
    return !this.isCurrentUserActiveChecker();
  });

  readonly canEditCheckInfo = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm || vm.status === CheckStatus.Abgeschlossen) return false;
    return this.isCurrentUserCreator() || this.isCurrentUserActiveChecker();
  });

  // Read-Only-Sperren für vergangene Kontrollphasen
  readonly isPhase11Locked = computed(() =>
    (this.contextStore.checkInfoViewModel()?.status ?? 0) >= CheckStatus['Kontrolle 1-2']
  );
  readonly isPhase12Locked = computed(() =>
    (this.contextStore.checkInfoViewModel()?.status ?? 0) >= CheckStatus['Kontrolle 2']
  );
  readonly isPhase2Locked = computed(() =>
    (this.contextStore.checkInfoViewModel()?.status ?? 0) >= CheckStatus.Abgeschlossen
  );

  readonly checkerIsCreator = computed(() => {
    const check = this.contextStore.checkInfoViewModel();
    if (!check) return false;
    return check.creator?.oid === check.checker12?.oid;
  });

  // Gutachterliche Tätigkeit – Weitergeben blockiert wenn Creator === Checker12
  // und die Checkbox "Gutachterliche Tätigkeit" nicht angehakt ist.
  // Gilt NUR in Kontrolle 1-1: in späteren Phasen ist der Stepper zurückgesetzt und
  // die Warnung wäre immer true, da checkerIsConsultant() default false ist.
  readonly hasExpertiseWarning = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm || vm.status !== CheckStatus['Kontrolle 1-1']) return false;
    return this.checkerIsCreator() && !this.checkStepperService.checkerIsConsultant();
  });

  // Prüfung, ob Checker 2 bei "Gutachterliche Tätigkeit" gesetzt wurde
  readonly existChecker2WithExpertise = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm || vm.status !== CheckStatus['Kontrolle 1-1'] && this.hasExpertiseWarning()) return false;
    return vm.checker2 == null;
  });

  // --- Validierung: Nachfolgender Prüfer muss gesetzt sein ---
  readonly hasNextChecker = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm) return false;
    if (vm.status === CheckStatus['Kontrolle 1-1']) {
      // Nächster Prüfer ist checker12 (außer bei Gutachter → checker2)
      return vm.isConsultant ? vm.checker2 != null : vm.checker12 != null;
    }
    if (vm.status === CheckStatus['Kontrolle 1-2']) {
      return vm.checker2 != null;
    }
    // K2 → Abgeschlossen: kein nachfolgender Prüfer nötig
    return true;
  });

  // --- Validierung: Zukünftige Prüffrist muss gesetzt sein ---
  readonly hasNextDueDate = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm) return false;
    if (vm.status === CheckStatus['Kontrolle 1-2']) {
      return vm.dueDate2 != null;
    }
    // K1-1 und K2 → Abgeschlossen: keine weitere Prüffrist nötig
    return true;
  });

  // --- Validierung: Prüffrist 2 darf nicht vor Prüffrist 1 liegen ---
  readonly isDueDateOrderValid = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm) return true;
    if (vm.dueDate1 == null || vm.dueDate2 == null) return true;
    const d1 = new Date(vm.dueDate1);
    const d2 = new Date(vm.dueDate2);
    return d2 >= d1;
  });

  // --- Beteiligung am Check ---
  readonly isCurrentUserInvolvedInCheck = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    const userOid = this.currentUserService.currentUser()?.oid;
    if (!vm || userOid == null) return false;
    return [vm.creator?.oid, vm.checker11?.oid, vm.checker12?.oid, vm.checker2?.oid].includes(userOid);
  });

  // --- Info Panels ---

  /** Zeigt das "Weitergegeben"-Info-Panel für alle Nutzer, die gerade nicht aktiv prüfen. */
  readonly showForwardedInfoPanel = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm) return false;
    if (vm.status === CheckStatus.Abgeschlossen) return false;
    if (this.isInRework()) return false;
    if (this.isCurrentUserActiveChecker()) return false;
    return true;
  });

  /** Zeigt das "Abgeschlossen"-Info-Panel für alle Beteiligten. */
  readonly showCompletedInfoPanel = computed(() => {
    const vm = this.contextStore.checkInfoViewModel();
    if (!vm) return false;
    return vm.status === CheckStatus.Abgeschlossen && this.isCurrentUserInvolvedInCheck();
  });

  readonly canForwardCheck = computed(() => {
    const currentCheck = this.contextStore.currentCheck();
    if (!currentCheck) return false;

    // Während Rework kann niemand weitergeben
    if (this.isInRework()) return false;

    // Nur der aktive Prüfer der aktuellen Phase darf weitergeben
    if (!this.isCurrentUserActiveChecker()) return false;

    // Gutachterliche Tätigkeit muss bestätigt sein
    if (this.hasExpertiseWarning()) return false;

    // Prüfen, ob Prüfer 2 bei "Gutachterlicher Tätigkeit" gesetzt wurde
    if (this.existChecker2WithExpertise()) return false;

    // Nachfolgender Prüfer muss gesetzt sein
    if (!this.hasNextChecker()) return false;

    // Zukünftige Prüffrist muss gesetzt sein
    if (!this.hasNextDueDate()) return false;

    // Prüffrist-Reihenfolge muss korrekt sein
    if (!this.isDueDateOrderValid()) return false;

    if (currentCheck.status === CheckStatus['Kontrolle 1-1']) {
      return this.checkStepperService.selfCheckDone();
    }
    return this.commentsResolved();
  });
}
